import { Box, Button, Menu, MenuItem, Select, Stack, SxProps, Tooltip, Typography, styled } from "@mui/material";
import _ from "lodash";
import { useState } from "react";
import { StatusIcon } from "src/components";
import { IStatus } from "../constants";

const SelectOptionBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  & .MuiListItemIcon-root {
    min-width: 25px;
  }
`;

interface IProps {
  options: IStatus[];
  value: number;
  variant: "button" | "select";
  groupBy?: (option: IStatus) => string;
  onChange: (statusId: number) => void;
  disabled?: boolean;
  sx?: SxProps;
}

const StatusPicker = (props: IProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const groupedOptions = props.groupBy ? _.groupBy(props.options, props.groupBy) : undefined;
  const selectedOption = props.options.find((option) => option.id === props.value);

  if (selectedOption === undefined) {
    throw new Error("Status not found in the options array");
  }

  function handleStatusChanged(value: number) {
    props.onChange(value);
  }

  function renderButtonMenuItem(option: IStatus) {
    return (
      <MenuItem
        key={option.id}
        onClick={(e) => {
          handleStatusChanged(option.id);
          setMenuAnchor(null);
          e.stopPropagation();
        }}
        sx={{ px: 0.25 }}
      >
        {renderMenuItem(option.id)}
      </MenuItem>
    );
  }

  function renderSelectMenuItem(option: IStatus) {
    return (
      <MenuItem key={option.id} value={option.id} sx={{ px: 0.25 }}>
        {renderMenuItem(option.id)}
      </MenuItem>
    );
  }

  function renderMenuItem(value: number) {
    const status = props.options.find((option) => option.id === value);

    if (status === undefined) {
      throw new Error("Value not found in the options array");
    }

    return (
      <Stack direction="row" spacing={0.5} alignItems="baseline">
        <StatusIcon status={status} sx={{ alignSelf: "center" }} />
        <Typography variant="body1">{status.name}</Typography>
      </Stack>
    );
  }

  if (props.variant === "button") {
    return (
      <>
        <Tooltip title={selectedOption.name}>
          <span>
            <Button
              disabled={props.disabled}
              variant="text"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setMenuAnchor(event.currentTarget);
                event.preventDefault();
              }}
              sx={{ minWidth: 26, mr: 0.25, ...props.sx }}
              disableRipple
            >
              <StatusIcon sx={{ height: "16px", width: "16px" }} status={selectedOption} />
            </Button>
          </span>
        </Tooltip>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={(event: React.MouseEvent) => {
            event.stopPropagation();
            setMenuAnchor(null);
          }}
        >
          {groupedOptions
            ? _.keys(groupedOptions).map((groupName) => (
                <Box key={groupName}>
                  <Typography variant="overline" sx={{ mx: 0.25, mt: 0.25 }}>
                    {groupName}
                  </Typography>
                  {groupedOptions[groupName].map((option) => renderButtonMenuItem(option))}
                </Box>
              ))
            : props.options.map((option) => renderButtonMenuItem(option))}
        </Menu>
      </>
    );
  }

  if (props.variant === "select") {
    return (
      <Select
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          disableScrollLock: true,
        }}
        variant="filled"
        disableUnderline
        disabled={props.disabled}
        value={props.value ?? ""}
        onChange={(e) => handleStatusChanged(e.target.value)}
        renderValue={(value) => <SelectOptionBox>{renderMenuItem(value)}</SelectOptionBox>}
        sx={{ ...props.sx }}
        fullWidth
      >
        {groupedOptions
          ? _.keys(groupedOptions).reduce((acc, groupName, index) => {
              acc.push(
                <Typography key={groupName} variant="overline" sx={{ mx: 0.25, mt: index > 0 ? 0.75 : 0.25 }}>
                  {groupName}
                </Typography>
              );

              acc.push(groupedOptions[groupName].map((option) => renderSelectMenuItem(option)));

              return acc;
            }, [] as React.ReactNode[])
          : props.options.map((option) => renderSelectMenuItem(option))}
      </Select>
    );
  }

  return null;
};

export { StatusPicker };
